import {Component, Inject, Input} from '@angular/core';
import {AbstractControl, FormGroup} from '@angular/forms';
import {MxFormErrorMessageService} from '../../../services/mx-form-error-message.service';
import {MxFieldConfig} from './mx-field-customizer.service';
import {MX_TRANSLATE_SERVICE_TOKEN, MxTranslateService} from '../../../../../shared/services/mx-translate.service';

export type MxLabelFn = () => string;
@Component({template:''})
export abstract class MxField {

    @Input() name: string;
    @Input() label: string | MxLabelFn;
    @Input() hidden: boolean = false;
    @Input() hint: string;
    @Input() form: FormGroup;
    @Input() readonly: boolean = false;
    @Input() formReadonlyMode: boolean = false;
    @Input() fitContent: boolean = false;
    @Input() fieldConfig: MxFieldConfig;
    @Input() rows: number = 4;
    @Input() errorMessage: boolean = true;
    @Input() informationKey: string;

    get required(): boolean {
        return this.hasRequiredField(this.form.controls[this.name]);
    }

    hasRequiredField = (abstractControl: AbstractControl): boolean => {
        const controls = 'controls';
        if (abstractControl.validator) {
            const validator = abstractControl.validator({}as AbstractControl);
            if (validator && validator.required) {
                return true;
            }
        }
        let allControls = abstractControl[controls];
        if (allControls) {
            for (const controlName in allControls) {
                if (allControls[controlName] && this.hasRequiredField(allControls[controlName])) {
                    return true;
                }
            }
        }
        return false;
    };

    protected constructor(protected errorService: MxFormErrorMessageService,
                          @Inject(MX_TRANSLATE_SERVICE_TOKEN) protected translateService?: MxTranslateService) {
    }

    public getValidationHint = (): string => {
        let result = this.getValidationMessages();
        result = result.map((message) => {
            return this.translateService ? this.translateService.getTranslatedLabel(message): message;
        });
        return result.join(". ").toString();
    };

    public getValidationMessages = (canHideMessage: boolean = false): string[] => {
        if (!this.form) {
            return [];
        }
        if (!this.errorMessage && canHideMessage) {
            return [];
        }
        return this.errorService.getMessages(this.name, this.form);
    };

    public resetValue(event: any): void {
        if (!this.form) {
            return;
        }

        let componentControl: AbstractControl = this.form.controls[this.name];
        componentControl.reset();
        componentControl.markAsTouched();
        componentControl.markAsDirty();

        event.stopPropagation();
    }

    public isDisplayed(): boolean {
        if (!this.form) {
            return true;
        }

        let componentControl: AbstractControl = this.form.controls[this.name];
        let value = componentControl.value;
        if (componentControl.disabled) {
            return false;
        }
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        if (value === '') {
            return false;
        }
        if (value != null) {
            return (typeof value !== 'object'
                || (typeof value === 'object' && value.key === undefined)
                || (typeof value === 'object' && value.key));
        }
        return false;
    }

    public getDisplayValue(value: any): any {
        let returnValue = value;
        if (!value || value === '') {
            returnValue = 'N/A';
        }
        if (this.fieldConfig && this.fieldConfig.displayFormat) {
            returnValue = this.fieldConfig.displayFormat(value);
        }
        return returnValue;
    }


    public defaultClickHandler(): void {
        if (this.fieldConfig && this.fieldConfig.clickHandler) {
            let control = this.form.get([this.name]);
            let callBackFn = (newValue: any) => {
                if (newValue !== undefined && control.value !== newValue) {
                    this.form.get([this.name]).patchValue(newValue);
                    this.form.get([this.name]).markAsDirty();
                }
            };
            this.fieldConfig.clickHandler(callBackFn, control.value);
        }
    }

    public withIcon(): string | boolean {
        if (this.fieldConfig && this.fieldConfig.icon) {
            return this.fieldConfig.icon;
        } else {
            return false;
        }
    }

    public getError  = (error: string): string => {
        return this.translate(error);
    };

    public getHint  = (): string => {
        return this.translate(this.hint);
    };

    public getLabel  = (): string => {
        return (typeof this.label === 'function')
            ? this.label()
            : this.translate(this.label);
    };

    private translate = (value: string): string => {
        return (this.translateService) ? this.translateService.getTranslatedLabel(value) : value;
    };
}
